import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { FileWarning, Download, Paperclip, Plus, Search, Printer, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAtLeast, useEffectiveRole } from "../../lib/roles";
import { hasActionAccess } from "../../lib/workspaces";

type Staff = { id: string; name: string };
type Attachment = { id: string; filename: string; size: number; uploaded_by_name?: string; created_at: string };
type CaseNote = { id: string; note: string; author_name: string; created_at: string };
type IRResponse = {
  what_happened?: string;
  contributing_factors?: string;
  information_to_consider?: string;
  corrective_preventive_action?: string;
};
type IRAddendum = { id: string; text: string; typed_signature: string; created_at: string };
type IRRecord = {
  id: string; case_id: string; case_number: string; notice_title: string; concern_summary: string;
  questions?: string; policy_reference?: string; incident_at?: string; due_at?: string; status: string;
  requested_at?: string; acknowledged_at?: string; acknowledged_signature?: string;
  draft_response?: IRResponse; submitted_response?: IRResponse; submitted_at?: string;
  submitted_signature?: string; closed_without_response_at?: string; closed_without_response_reason?: string;
  shared_evidence?: Attachment[]; attachments?: Attachment[]; addenda?: IRAddendum[]; version: number;
};
type CaseRow = {
  id: string; case_number: string; status: string; priority: string; issue_type: string;
  involved_party_type?: string; involved_party_name?: string; involved_staff_user_id?: string;
  summary?: string; finding?: string; outcome?: string; submitted_at?: string;
  created_at?: string; updated_at: string; version?: number;
};
type CaseDetail = CaseRow & {
  complainant_type: string; complainant_name?: string; complainant_contact?: string;
  details: string; incident_at?: string; submitted_by_name?: string;
  assigned_investigator_user_id?: string; management_summary?: string;
  root_cause?: string; resolution?: string; attachments: Attachment[]; notes: CaseNote[];
  linked_coaching_id?: string; ir?: IRRecord | null; version: number;
};

const COMPLAINANT_TYPES = [
  ["patient_client", "Patient / Client"],
  ["staff_colleague", "Staff / Colleague"],
  ["management", "Management"],
  ["supplier_vendor", "Supplier / Vendor"],
  ["partner", "Partner"],
  ["anonymous", "Anonymous"],
  ["other", "Other"],
] as const;
const INVOLVED_TYPES = [
  ["staff", "Staff member"],
  ["patient_client", "Patient / Client"],
  ["supplier_vendor", "Supplier / Vendor"],
  ["partner", "Partner"],
  ["other", "Other"],
] as const;
const ISSUE_TYPES = [
  ["conduct_behavior", "Conduct / Behavior"],
  ["communication", "Communication"],
  ["knowledge_training", "Knowledge / Training / Understanding"],
  ["process_procedure", "Process / Procedure"],
  ["service_patient_care", "Service / Patient Care"],
  ["policy_compliance", "Policy / Compliance"],
  ["safety", "Safety"],
  ["data_privacy", "Data / Privacy"],
  ["attendance_reliability", "Attendance / Reliability"],
  ["other", "Other"],
] as const;
const STATUSES = ["new", "acknowledged", "in_review", "awaiting_ir", "action_in_progress", "monitoring", "resolved", "closed"];
const FINDINGS = ["pending", "substantiated", "partially_substantiated", "unsubstantiated", "inconclusive", "process_system_issue"];
const OUTCOMES = ["pending", "no_action", "coaching", "training", "process_correction", "written_warning", "final_warning", "suspension", "termination_review", "terminated", "other"];
const ROOT_CAUSES = ["pending", "people", "training", "process", "policy", "system", "environment", "supplier", "other"];
const PRIORITIES = ["low", "moderate", "high", "critical"];

const blankIRRequest = {
  notice_title: "Notice of Concern / Incident Report Request",
  concern_summary: "",
  questions: "Please explain what happened from your perspective.\nWhat circumstances contributed to the event?\nIs there any information management should consider?\nWhat corrective or preventive action do you recommend?",
  policy_reference: "",
  due_at: "",
  shared_evidence_attachment_ids: [] as string[],
};
const blankIRResponse: IRResponse = {
  what_happened: "", contributing_factors: "", information_to_consider: "", corrective_preventive_action: "",
};
const blankCoaching = { title: "Complaint follow-up coaching", feedback: "", action: "", due_date: "", follow_up_at: "" };

const blankForm = {
  complainant_type: "patient_client",
  complainant_name: "",
  complainant_contact: "",
  involved_party_type: "staff",
  involved_staff_user_id: "",
  involved_party_name: "",
  issue_type: "conduct_behavior",
  summary: "",
  details: "",
  incident_at: "",
  priority: "moderate",
};
const pretty = (value?: string) => (value || "—").replace(/_/g, " ");
const when = (value?: string) => value ? new Date(value).toLocaleString() : "—";
const toIso = (value: string) => value ? new Date(value).toISOString() : null;
const esc = (value: unknown) => String(value || "").replace(/[&<>"\']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "\'": "&#039;" }[char] || char));

export default function ComplaintCases() {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const [searchParams] = useSearchParams();
  const allowed = canAtLeast(role, "representative");
  const admin = role === "owner_admin" || role === "admin_support";
  const canAdd = allowed && hasActionAccess(user, "complaints", "add");
  const canEdit = admin && hasActionAccess(user, "complaints", "edit");
  const canRespond = allowed && hasActionAccess(user, "complaints", "edit") && hasActionAccess(user, "complaints", "add");
  const canAddNote = admin && hasActionAccess(user, "complaints", "add");
  const [rows, setRows] = useState<CaseRow[]>([]);
  const [myIRs, setMyIRs] = useState<IRRecord[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [investigators, setInvestigators] = useState<Staff[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [manage, setManage] = useState({
    status: "new", priority: "moderate", assigned_investigator_user_id: "",
    management_summary: "", finding: "pending", outcome: "pending", root_cause: "pending", resolution: "",
  });
  const [note, setNote] = useState("");
  const [irRequestOpen, setIrRequestOpen] = useState(false);
  const [irRequest, setIrRequest] = useState(blankIRRequest);
  const [staffIR, setStaffIR] = useState<IRRecord | null>(null);
  const [irResponse, setIrResponse] = useState<IRResponse>(blankIRResponse);
  const [ackSignature, setAckSignature] = useState("");
  const [submitSignature, setSubmitSignature] = useState("");
  const [addendum, setAddendum] = useState("");
  const [addendumSignature, setAddendumSignature] = useState("");
  const [closeIROpen, setCloseIROpen] = useState(false);
  const [closeIRReason, setCloseIRReason] = useState("");
  const [coachingOpen, setCoachingOpen] = useState(false);
  const [coachingForm, setCoachingForm] = useState(blankCoaching);

  const load = async () => {
    if (!allowed) { setRows([]); return; }
    try {
      const { data } = await api.get("/complaint-cases", { params: admin ? { q: q.trim(), status, priority } : {} });
      setRows(data.items || []);
    } catch (e) { toastError(e); }
  };

  const loadMyIRs = async () => {
    if (!allowed) { setMyIRs([]); return; }
    try {
      const { data } = await api.get("/complaint-cases/inbox/my-ir");
      setMyIRs(data.items || []);
    } catch (e) { toastError(e); }
  };

  useEffect(() => { void load(); }, [allowed, admin, status, priority]);
  useEffect(() => { void loadMyIRs(); }, [allowed, admin]);

  useEffect(() => {
    if (!allowed) { setStaff([]); setInvestigators([]); return; }
    api.get("/complaint-cases/staff-options")
      .then(({ data }) => setStaff(data || []))
      .catch(() => setStaff([]));
    if (admin) {
      api.get("/complaint-cases/investigator-options")
        .then(({ data }) => setInvestigators(data || []))
        .catch(() => setInvestigators([]));
    } else {
      setInvestigators([]);
    }
  }, [allowed, admin]);

  const uploadEvidenceFiles = async (caseId: string, selected: File[]) => {
    const failed: string[] = [];
    for (const file of selected) {
      const body = new FormData();
      body.append("file", file);
      try {
        await api.post("/complaint-cases/" + caseId + "/attachments", body);
      } catch {
        failed.push(file.name);
      }
    }
    if (failed.length) {
      toast.warning(`Case saved, but ${failed.length} evidence file(s) could not be uploaded: ${failed.join(", ")}. You can add evidence to the saved case without submitting a duplicate complaint.`);
    }
    return failed;
  };

  const createCase = async () => {
    if (!canAdd || saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        complainant_name: form.complainant_type === "anonymous" ? null : (form.complainant_name.trim() || null),
        complainant_contact: form.complainant_type === "anonymous" ? null : (form.complainant_contact.trim() || null),
        involved_staff_user_id: form.involved_party_type === "staff" ? form.involved_staff_user_id : null,
        involved_party_name: form.involved_party_type === "staff" ? null : (form.involved_party_name.trim() || null),
        incident_at: form.incident_at || null,
      };
      const { data } = await api.post("/complaint-cases", payload);
      await uploadEvidenceFiles(data.id, files);
      toast.success("Complaint submitted as " + data.case_number + ".");
      setOpen(false);
      setForm(blankForm);
      setFiles([]);
      await load();
      if (admin) await openReview(data.id);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const openReview = async (id: string) => {
    if (!admin) return;
    try {
      const { data } = await api.get<CaseDetail>("/complaint-cases/" + id);
      setDetail(data);
      setManage({
        status: data.status || "new",
        priority: data.priority || "moderate",
        assigned_investigator_user_id: data.assigned_investigator_user_id || "",
        management_summary: data.management_summary || "",
        finding: data.finding || "pending",
        outcome: data.outcome || "pending",
        root_cause: data.root_cause || "pending",
        resolution: data.resolution || "",
      });
    } catch (e) { toastError(e); }
  };

  const openStaffIR = async (caseId: string) => {
    try {
      const { data } = await api.get<IRRecord>("/complaint-cases/" + caseId + "/ir");
      setStaffIR(data);
      setIrResponse(data.submitted_response || data.draft_response || blankIRResponse);
    } catch (e) { toastError(e); }
  };

  useEffect(() => {
    const caseId = searchParams.get("case");
    const irCaseId = searchParams.get("ir");
    if (admin && caseId) void openReview(caseId);
    if (irCaseId) void openStaffIR(irCaseId);
  }, [admin, searchParams]);

  const saveReview = async () => {
    if (!detail || !canEdit || saving) return;
    setSaving(true);
    try {
      await api.patch("/complaint-cases/" + detail.id, {
        version: detail.version,
        ...manage,
        assigned_investigator_user_id: manage.assigned_investigator_user_id || null,
        management_summary: manage.management_summary.trim() || null,
        resolution: manage.resolution.trim() || null,
      });
      toast.success("Complaint case updated.");
      await load();
      await openReview(detail.id);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!detail || !note.trim() || !canAddNote) return;
    try {
      await api.post("/complaint-cases/" + detail.id + "/notes", { note: note.trim() });
      setNote("");
      toast.success("Confidential investigation note added.");
      await openReview(detail.id);
    } catch (e) { toastError(e); }
  };

  const downloadEvidence = async (attachment: Attachment) => {
    if (!detail || !admin) return;
    try {
      const response = await api.get("/complaint-cases/" + detail.id + "/attachments/" + attachment.id + "/file", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toastError(e); }
  };

  const beginIRRequest = () => {
    if (!detail) return;
    setIrRequest({
      ...blankIRRequest,
      concern_summary: manage.management_summary.trim() || detail.management_summary || "",
    });
    setIrRequestOpen(true);
  };

  const requestIR = async () => {
    if (!detail || !irRequest.concern_summary.trim() || saving) return;
    setSaving(true);
    try {
      await api.post("/complaint-cases/" + detail.id + "/request-ir", {
        version: detail.version,
        ...irRequest,
        notice_title: irRequest.notice_title.trim(),
        concern_summary: irRequest.concern_summary.trim(),
        questions: irRequest.questions.trim() || null,
        policy_reference: irRequest.policy_reference.trim() || null,
        due_at: toIso(irRequest.due_at),
      });
      toast.success("Incident Report requested from the staff member.");
      setIrRequestOpen(false);
      await load();
      await openReview(detail.id);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const toggleSharedEvidence = (id: string) => {
    setIrRequest((current) => ({
      ...current,
      shared_evidence_attachment_ids: current.shared_evidence_attachment_ids.includes(id)
        ? current.shared_evidence_attachment_ids.filter((value) => value !== id)
        : [...current.shared_evidence_attachment_ids, id],
    }));
  };

  const acknowledgeIR = async () => {
    if (!staffIR || !ackSignature.trim()) return;
    try {
      const { data } = await api.post("/complaint-cases/" + staffIR.case_id + "/ir/acknowledge", {
        version: staffIR.version, typed_signature: ackSignature.trim(), confirmed: true,
      });
      setStaffIR(data);
      setAckSignature("");
      toast.success("Notice receipt acknowledged.");
      await loadMyIRs();
    } catch (e) { toastError(e); }
  };

  const saveIRDraft = async () => {
    if (!staffIR || !canRespond || staffIR.submitted_at) return;
    try {
      const { data } = await api.put("/complaint-cases/" + staffIR.case_id + "/ir/draft", {
        version: staffIR.version, ...irResponse,
      });
      setStaffIR(data);
      setIrResponse(data.draft_response || irResponse);
      toast.success("IR draft saved.");
      await loadMyIRs();
    } catch (e) { toastError(e); }
  };

  const submitIR = async () => {
    if (!staffIR || !submitSignature.trim() || !staffIR.acknowledged_at || !String(irResponse.what_happened || "").trim()) return;
    setSaving(true);
    try {
      let current = staffIR;
      if (!staffIR.submitted_at) {
        const draft = await api.put("/complaint-cases/" + staffIR.case_id + "/ir/draft", {
          version: staffIR.version, ...irResponse,
        });
        current = draft.data;
      }
      const { data } = await api.post("/complaint-cases/" + current.case_id + "/ir/submit", {
        version: current.version, typed_signature: submitSignature.trim(), confirmed: true,
      });
      setStaffIR(data);
      setSubmitSignature("");
      toast.success("Incident Report submitted. The original response is now locked.");
      await loadMyIRs();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const closeIRWithoutResponse = async () => {
    if (!detail?.ir || !closeIRReason.trim() || !admin) return;
    setSaving(true);
    try {
      await api.post("/complaint-cases/" + detail.id + "/ir/close-without-response", {
        version: detail.ir.version, reason: closeIRReason.trim(),
      });
      toast.success("IR closed without a staff response. The case returned to In Review.");
      setCloseIROpen(false);
      setCloseIRReason("");
      await load();
      await openReview(detail.id);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const submitAddendum = async () => {
    if (!staffIR || !addendum.trim() || !addendumSignature.trim()) return;
    try {
      await api.post("/complaint-cases/" + staffIR.case_id + "/ir/addenda", {
        text: addendum.trim(), typed_signature: addendumSignature.trim(), confirmed: true,
      });
      setAddendum("");
      setAddendumSignature("");
      toast.success("IR addendum added without changing the original response.");
      await openStaffIR(staffIR.case_id);
      await loadMyIRs();
    } catch (e) { toastError(e); }
  };

  const uploadIRSupportingFiles = async (selected: File[]) => {
    if (!staffIR || staffIR.submitted_at) return;
    const failed: string[] = [];
    for (const file of selected) {
      const body = new FormData();
      body.append("file", file);
      try {
        await api.post("/complaint-cases/" + staffIR.case_id + "/ir/attachments", body);
      } catch {
        failed.push(file.name);
      }
    }
    if (failed.length) toast.warning(failed.length + " IR attachment(s) could not be uploaded: " + failed.join(", "));
    await openStaffIR(staffIR.case_id);
  };

  const downloadIRAttachment = async (caseId: string, attachment: Attachment) => {
    try {
      const response = await api.get(
        "/complaint-cases/" + caseId + "/ir/attachments/" + attachment.id + "/file",
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url; link.download = attachment.filename; link.click(); URL.revokeObjectURL(url);
    } catch (e) { toastError(e); }
  };

  const downloadSharedEvidence = async (attachment: Attachment) => {
    if (!staffIR) return;
    try {
      const response = await api.get(
        "/complaint-cases/" + staffIR.case_id + "/ir/shared-evidence/" + attachment.id + "/file",
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url; link.download = attachment.filename; link.click(); URL.revokeObjectURL(url);
    } catch (e) { toastError(e); }
  };

  const printNotice = (ir: IRRecord) => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Allow pop-ups to print the Notice of Concern."); return; }
    win.opener = null;
    const html = [
      "<!doctype html><html><head><title>", esc(ir.notice_title), "</title><style>",
      "body{font-family:Arial,sans-serif;color:#172033;margin:36px;line-height:1.5}",
      "h1{font-size:22px;margin-bottom:4px}.meta{font-size:12px;color:#64748b;margin-bottom:24px}",
      "section{margin:20px 0}h2{font-size:12px;text-transform:uppercase;letter-spacing:.08em;border-bottom:1px solid #cbd5e1;padding-bottom:5px}",
      "p{white-space:pre-wrap}.notice{border:1px solid #cbd5e1;background:#f8fafc;padding:14px}",
      ".sig{margin-top:40px;border-top:1px solid #172033;padding-top:6px;width:45%}</style></head><body>",
      "<h1>", esc(ir.notice_title), "</h1><div class='meta'>Confidential staff notice · ", esc(ir.case_number),
      " · Requested ", esc(when(ir.requested_at)), "</div>",
      "<div class='notice'>This notice contains only the concern information approved for staff response. Complainant identity and confidential investigation material are intentionally excluded.</div>",
      "<section><h2>Concern requiring response</h2><p>", esc(ir.concern_summary), "</p></section>",
      ir.policy_reference ? "<section><h2>Relevant policy / process</h2><p>" + esc(ir.policy_reference) + "</p></section>" : "",
      ir.questions ? "<section><h2>Questions to address</h2><p>" + esc(ir.questions) + "</p></section>" : "",
      "<section><h2>Response deadline</h2><p>", esc(when(ir.due_at)), "</p></section>",
      "<div class='sig'>Management / date</div><div class='sig'>Employee acknowledgment / date</div>",
      "<script>window.print();</script></body></html>"
    ].join("");
    win.document.write(html);
    win.document.close();
  };

  const createLinkedCoaching = async () => {
    if (!detail || role !== "owner_admin" || !coachingForm.action.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/complaint-cases/" + detail.id + "/create-coaching", {
        version: detail.version,
        ...coachingForm,
        feedback: coachingForm.feedback.trim() || null,
        due_date: coachingForm.due_date ? toIso(coachingForm.due_date) : null,
        follow_up_at: coachingForm.follow_up_at ? toIso(coachingForm.follow_up_at) : null,
      });
      toast.success(data.already_exists ? "Linked coaching already exists." : "Linked coaching created.");
      setCoachingOpen(false);
      setCoachingForm(blankCoaching);
      await load();
      await openReview(detail.id);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const validCreate = form.summary.trim().length >= 3 && form.details.trim().length >= 3 &&
    (form.involved_party_type === "staff" ? Boolean(form.involved_staff_user_id) : Boolean(form.involved_party_name.trim()));

  if (!allowed) {
    return <div className="mx-auto max-w-3xl p-8 text-sm text-slate-600">You do not have access to submit or review complaint cases.</div>;
  }

  return <>
    <PageHeader
      title={admin ? "Complaints & Case Management" : "Report a Concern"}
      subtitle={admin
        ? "Confidential intake, investigation, IR responses, findings, and resolution"
        : "Submit concerns securely and respond to any private IR request assigned to you"}
      actions={canAdd ? <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-full" data-testid="new-complaint-case"><Plus className="mr-1 h-4 w-4" /> New complaint</Button></DialogTrigger>
        <DialogContent className="max-h-[92dvh] max-w-3xl overflow-auto" data-testid="complaint-intake-dialog">
          <DialogHeader><DialogTitle>New complaint / concern</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700">
              Complainant identity, original complaint details, and uploaded proof are restricted to complaint administrators for investigation.
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div><Label>Complainant type</Label><Select value={form.complainant_type} onValueChange={(v) => setForm({ ...form, complainant_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{COMPLAINANT_TYPES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
            </div>
            {form.complainant_type !== "anonymous" && <div className="grid gap-3 md:grid-cols-2">
              <div><Label>Complainant name <span className="text-slate-400">(admin-only)</span></Label><Input value={form.complainant_name} onChange={(e) => setForm({ ...form, complainant_name: e.target.value })} /></div>
              <div><Label>Complainant contact <span className="text-slate-400">(admin-only)</span></Label><Input value={form.complainant_contact} onChange={(e) => setForm({ ...form, complainant_contact: e.target.value })} /></div>
            </div>}
            <div className="grid gap-3 md:grid-cols-2">
              <div><Label>Involved party</Label><Select value={form.involved_party_type} onValueChange={(v) => setForm({ ...form, involved_party_type: v, involved_staff_user_id: "", involved_party_name: "" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{INVOLVED_TYPES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
              {form.involved_party_type === "staff"
                ? <div><Label>Staff member</Label><Select value={form.involved_staff_user_id} onValueChange={(v) => setForm({ ...form, involved_staff_user_id: v })}><SelectTrigger><SelectValue placeholder="Choose staff" /></SelectTrigger><SelectContent>{staff.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></div>
                : <div><Label>Name of involved party</Label><Input value={form.involved_party_name} onChange={(e) => setForm({ ...form, involved_party_name: e.target.value })} /></div>}
            </div>
            <div><Label>Issue type</Label><Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ISSUE_TYPES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Short summary</Label><Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} maxLength={500} /></div>
            <div><Label>Complaint details</Label><Textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="min-h-32" /></div>
            <div><Label>Incident date and time <span className="text-slate-400">(if known)</span></Label><Input type="datetime-local" value={form.incident_at} onChange={(e) => setForm({ ...form, incident_at: e.target.value })} /></div>
            <div><Label>Proof / evidence</Label><label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed p-3 text-sm text-slate-600"><Paperclip className="h-4 w-4" /> {files.length ? files.length + " file(s) selected" : "Attach screenshots, transcripts, PDF, DOC/DOCX, or text files"}<input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp" onChange={(e) => setFiles(Array.from(e.target.files || []))} /></label><p className="mt-1 text-xs text-slate-500">Up to 10 MB per file. Uploaded proof is not visible to the involved employee. An admin may explicitly share a specific file in the IR notice when it is necessary for a fair response.</p></div>
          </div>
          <DialogFooter><Button onClick={createCase} disabled={!validCreate || saving}>{saving ? "Submitting…" : "Submit for admin review"}</Button></DialogFooter>
        </DialogContent>
      </Dialog> : undefined}
    />

    <div className="mx-auto max-w-7xl space-y-5 p-6 md:p-8">
      {admin && myIRs.length > 0 && <section className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="admin-personal-ir-requests">
        <div className="mb-3"><h2 className="font-semibold">My personal Incident Report requests</h2><p className="text-xs text-slate-600">These are cases where you are the involved staff member. Your administrator role does not grant access to the raw investigation file for your own case.</p></div>
        <div className="space-y-2">{myIRs.map((ir) => <button key={ir.id} type="button" className="flex w-full items-center justify-between gap-3 rounded-md border bg-white p-3 text-left hover:border-slate-400" onClick={() => void openStaffIR(ir.case_id)}>
          <div><div className="text-sm font-medium">{ir.notice_title}</div><div className="mt-1 text-xs text-slate-500">{ir.case_number} · {pretty(ir.status)} · Due {when(ir.due_at)}</div></div>
          <span className="text-xs font-medium text-blue-700">Open my IR</span>
        </button>)}</div>
      </section>}

      {admin && <div className="grid gap-3 rounded-lg border bg-white p-4 md:grid-cols-4">
        <div className="md:col-span-2"><Label>Search cases</Label><div className="flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case number, summary, involved party" onKeyDown={(e) => { if (e.key === "Enter") void load(); }} /><Button variant="outline" onClick={() => void load()}><Search className="h-4 w-4" /></Button></div></div>
        <div><Label>Status</Label><Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Priority</Label><Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{PRIORITIES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
      </div>}

      {!admin && <>
        <div className="rounded-md border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          Submitted complaints remain private. IR requests below show only the management-approved concern you need to answer; complainant identity and restricted investigation material are not exposed.
        </div>
        <section className="rounded-lg border bg-white p-4" data-testid="my-ir-requests">
          <div className="mb-3 flex items-center justify-between">
            <div><h2 className="font-semibold">My Incident Report requests</h2><p className="text-xs text-slate-500">Acknowledge the notice, save your draft, then sign and submit your response.</p></div>
            <Button variant="outline" size="sm" onClick={() => void loadMyIRs()}>Refresh</Button>
          </div>
          {myIRs.length === 0 ? <p className="text-sm text-slate-500">No IR requests assigned to you.</p> :
            <div className="space-y-2">{myIRs.map((ir) => <button key={ir.id} type="button" className="flex w-full items-center justify-between gap-3 rounded-md border p-3 text-left hover:border-slate-400" onClick={() => void openStaffIR(ir.case_id)}>
              <div><div className="text-sm font-medium">{ir.notice_title}</div><div className="mt-1 text-xs text-slate-500">{ir.case_number} · {pretty(ir.status)} · Due {when(ir.due_at)}</div></div>
              <span className="text-xs font-medium text-blue-700">Open IR</span>
            </button>)}</div>}
        </section>
      </>}

      {rows.length === 0 ? <EmptyState icon={FileWarning} title={admin ? "No complaint cases found" : "No complaints submitted by you"} /> :
        <div className="overflow-hidden rounded-lg border bg-white">
          {!admin && <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold">My submitted concerns</div>}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
              <th className="px-4 py-3 text-left">Case</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Priority</th>
              <th className="px-4 py-3 text-left">Issue</th>{admin ? <><th className="px-4 py-3 text-left">Involved party</th><th className="px-4 py-3 text-left">Summary</th><th className="px-4 py-3 text-right">Action</th></> : <th className="px-4 py-3 text-right">Evidence</th>}
            </tr></thead>
            <tbody>{rows.map((row) => <tr key={row.id} className="border-t">
              <td className="px-4 py-3 font-medium">{row.case_number}</td>
              <td className="px-4 py-3 capitalize">{pretty(row.status)}</td>
              <td className="px-4 py-3 capitalize">{pretty(row.priority)}</td>
              <td className="px-4 py-3 capitalize">{pretty(row.issue_type)}</td>
              {admin && <><td className="px-4 py-3">{row.involved_party_name || pretty(row.involved_party_type)}</td><td className="max-w-md px-4 py-3">{row.summary}</td><td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => void openReview(row.id)}>Review</Button></td></>}
              {!admin && <td className="px-4 py-3 text-right">{canAdd && <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs"><Paperclip className="h-3.5 w-3.5" /> Add evidence<input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp" onChange={async (e) => { const selected = Array.from(e.target.files || []); if (!selected.length) return; await uploadEvidenceFiles(row.id, selected); e.target.value = ""; }} /></label>}</td>}
            </tr>)}</tbody>
          </table>
        </div>}
    </div>

    {detail && admin && <Dialog open onOpenChange={(value) => { if (!value) setDetail(null); }}>
      <DialogContent className="max-h-[94dvh] max-w-5xl overflow-auto" data-testid="complaint-review-dialog">
        <DialogHeader><DialogTitle>{detail.case_number} · Confidential complaint case</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-900"><strong>Restricted investigation record.</strong> Complainant identity, original complaint text, proof, and investigation notes must not be copied into staff-facing Coaching or User 360.</div>
          <div className="grid gap-4 md:grid-cols-2">
            <section className="rounded-lg border p-4"><h3 className="mb-3 font-semibold">Complaint intake</h3>
              <dl className="space-y-2 text-sm"><div><dt className="text-xs uppercase text-slate-500">Complainant</dt><dd>{pretty(detail.complainant_type)}{detail.complainant_name ? " · " + detail.complainant_name : ""}</dd></div>
              {detail.complainant_contact && <div><dt className="text-xs uppercase text-slate-500">Contact</dt><dd>{detail.complainant_contact}</dd></div>}
              <div><dt className="text-xs uppercase text-slate-500">Involved party</dt><dd>{detail.involved_party_name || pretty(detail.involved_party_type)}</dd></div>
              <div><dt className="text-xs uppercase text-slate-500">Incident</dt><dd>{when(detail.incident_at)}</dd></div>
              <div><dt className="text-xs uppercase text-slate-500">Submitted by</dt><dd>{detail.submitted_by_name || "—"}</dd></div></dl>
            </section>
            <section className="rounded-lg border p-4"><h3 className="mb-3 font-semibold">Original complaint</h3><div className="text-sm font-medium">{detail.summary}</div><p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{detail.details}</p></section>
          </div>

          <section className="rounded-lg border p-4"><div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">Confidential evidence</h3>{canAdd && <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-1.5 text-xs"><Paperclip className="h-4 w-4" /> Add evidence<input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp" onChange={async (e) => { const selected = Array.from(e.target.files || []); if (!selected.length) return; await uploadEvidenceFiles(detail.id, selected); e.target.value = ""; await openReview(detail.id); }} /></label>}</div>
            {detail.attachments.length === 0 ? <p className="text-sm text-slate-500">No evidence files attached.</p> : <div className="flex flex-wrap gap-2">{detail.attachments.map((a) => <Button key={a.id} variant="outline" size="sm" onClick={() => void downloadEvidence(a)}><Download className="mr-1 h-4 w-4" /> {a.filename}</Button>)}</div>}
          </section>

          <section className="space-y-3 rounded-lg border p-4"><h3 className="font-semibold">Administrative review</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <div><Label>Status</Label><Select value={manage.status} onValueChange={(v) => setManage({ ...manage, status: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={manage.priority} onValueChange={(v) => setManage({ ...manage, priority: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Assigned investigator</Label><Select value={manage.assigned_investigator_user_id || "unassigned"} onValueChange={(v) => setManage({ ...manage, assigned_investigator_user_id: v === "unassigned" ? "" : v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{investigators.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Management summary / verified concern</Label><Textarea value={manage.management_summary} onChange={(e) => setManage({ ...manage, management_summary: e.target.value })} disabled={!canEdit} placeholder="Use neutral, verified language. This may become the source for the staff-facing Notice of Concern." /></div>
            <div className="grid gap-3 md:grid-cols-3">
              <div><Label>Finding</Label><Select value={manage.finding} onValueChange={(v) => setManage({ ...manage, finding: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FINDINGS.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Outcome</Label><Select value={manage.outcome} onValueChange={(v) => setManage({ ...manage, outcome: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{OUTCOMES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Root cause</Label><Select value={manage.root_cause} onValueChange={(v) => setManage({ ...manage, root_cause: v })} disabled={!canEdit}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ROOT_CAUSES.map((v) => <SelectItem key={v} value={v}>{pretty(v)}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Resolution</Label><Textarea value={manage.resolution} onChange={(e) => setManage({ ...manage, resolution: e.target.value })} disabled={!canEdit} placeholder="Required before resolving or closing the case." /></div>
            {canEdit && <div className="flex justify-end"><Button onClick={saveReview} disabled={saving}>{saving ? "Saving…" : "Save review"}</Button></div>}
          </section>

          <section className="space-y-3 rounded-lg border p-4" data-testid="complaint-ir-section">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><h3 className="font-semibold">Notice of Concern & Incident Report</h3><p className="text-xs text-slate-500">Only sanitized concern information and explicitly shared evidence are visible to staff.</p></div>
              {!detail.ir && detail.involved_party_type === "staff" && canAdd && <Button size="sm" onClick={beginIRRequest}>Request IR</Button>}
            </div>
            {!detail.ir ? <p className="text-sm text-slate-500">{detail.involved_party_type === "staff" ? "No Incident Report has been requested." : "IR is available only when the involved party is a staff member."}</p> :
              <div className="space-y-3">
                <div className="grid gap-2 text-sm md:grid-cols-4">
                  <div><span className="text-xs uppercase text-slate-500">Status</span><div className="capitalize">{pretty(detail.ir.status)}</div></div>
                  <div><span className="text-xs uppercase text-slate-500">Requested</span><div>{when(detail.ir.requested_at)}</div></div>
                  <div><span className="text-xs uppercase text-slate-500">Due</span><div>{when(detail.ir.due_at)}</div></div>
                  <div><span className="text-xs uppercase text-slate-500">Submitted</span><div>{when(detail.ir.submitted_at)}</div></div>
                </div>
                <div className="rounded bg-slate-50 p-3 text-sm"><strong>{detail.ir.notice_title}</strong><p className="mt-2 whitespace-pre-wrap">{detail.ir.concern_summary}</p></div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => printNotice(detail.ir!)}><Printer className="mr-1 h-4 w-4" /> Print staff notice</Button>
                  {!detail.ir.submitted_at && detail.ir.status !== "closed_without_response" && canEdit && <Button variant="outline" size="sm" onClick={() => setCloseIROpen(true)}>Close IR without response</Button>}
                  {(detail.ir.shared_evidence || []).map((a) => <span key={a.id} className="rounded-md border px-2 py-1 text-xs">Shared with staff: {a.filename}</span>)}
                </div>
                {detail.ir.status === "closed_without_response" && <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900"><strong>IR closed without response.</strong> {detail.ir.closed_without_response_reason || "Reason recorded."} · {when(detail.ir.closed_without_response_at)}</div>}
                {detail.ir.acknowledged_at && <div className="text-sm text-emerald-700">Notice acknowledged by {detail.ir.acknowledged_signature} · {when(detail.ir.acknowledged_at)}</div>}
                {(detail.ir.attachments || []).length > 0 && <div className="rounded-md border p-3"><div className="mb-2 text-xs font-semibold uppercase text-slate-500">Staff IR supporting files</div><div className="flex flex-wrap gap-2">{detail.ir.attachments!.map((a) => <Button key={a.id} variant="outline" size="sm" onClick={() => void downloadIRAttachment(detail.id, a)}><Download className="mr-1 h-4 w-4" /> {a.filename}</Button>)}</div></div>}
                {detail.ir.submitted_response && <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
                  <div className="font-semibold">Submitted IR · signed by {detail.ir.submitted_signature}</div>
                  <div className="mt-2 space-y-2">
                    <p><strong>What happened:</strong> {detail.ir.submitted_response.what_happened || "—"}</p>
                    <p><strong>Contributing factors:</strong> {detail.ir.submitted_response.contributing_factors || "—"}</p>
                    <p><strong>Information to consider:</strong> {detail.ir.submitted_response.information_to_consider || "—"}</p>
                    <p><strong>Corrective/preventive action:</strong> {detail.ir.submitted_response.corrective_preventive_action || "—"}</p>
                  </div>
                </div>}
                {(detail.ir.addenda || []).length > 0 && <div className="space-y-2"><div className="text-xs font-semibold uppercase text-slate-500">IR addenda</div>{detail.ir.addenda!.map((a) => <div key={a.id} className="rounded bg-slate-50 p-3 text-sm"><p className="whitespace-pre-wrap">{a.text}</p><div className="mt-1 text-xs text-slate-500">{a.typed_signature} · {when(a.created_at)}</div></div>)}</div>}
              </div>}
          </section>

          <section className="space-y-3 rounded-lg border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><h3 className="font-semibold">Corrective / coaching action</h3><p className="text-xs text-slate-500">A complaint itself is not a performance penalty. Create coaching only after management review.</p></div>
              {role === "owner_admin" && !detail.linked_coaching_id && <Button size="sm" variant="outline" onClick={() => setCoachingOpen(true)}><GraduationCap className="mr-1 h-4 w-4" /> Create linked coaching</Button>}
            </div>
            {detail.linked_coaching_id ? <p className="text-sm text-emerald-700">Linked coaching created: {detail.linked_coaching_id}</p> : <p className="text-sm text-slate-500">{role === "admin_support" ? "Owner/Admin creates formal coaching after the investigation/IR review." : "No coaching is linked yet."}</p>}
          </section>

          <section className="space-y-3 rounded-lg border p-4"><h3 className="font-semibold">Confidential investigation notes</h3>
            {detail.notes.length === 0 ? <p className="text-sm text-slate-500">No investigation notes yet.</p> : <div className="space-y-2">{detail.notes.map((n) => <div key={n.id} className="rounded bg-slate-50 p-3 text-sm"><div className="whitespace-pre-wrap">{n.note}</div><div className="mt-1 text-xs text-slate-500">{n.author_name} · {when(n.created_at)}</div></div>)}</div>}
            {canAddNote && <div className="flex gap-2"><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an admin-only investigation note" /><Button onClick={addNote} disabled={!note.trim()}>Add note</Button></div>}
          </section>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>}

    <Dialog open={irRequestOpen} onOpenChange={setIrRequestOpen}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-auto" data-testid="request-ir-dialog">
        <DialogHeader><DialogTitle>Request Incident Report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900"><strong>Staff-facing document.</strong> Do not include the complainant's identity, contact details, raw complaint wording, or confidential investigation notes. The server also blocks exact complainant identity/contact if accidentally copied.</div>
          <div><Label>Notice title</Label><Input value={irRequest.notice_title} onChange={(e) => setIrRequest({ ...irRequest, notice_title: e.target.value })} /></div>
          <div><Label>Sanitized concern / allegation</Label><Textarea className="min-h-28" value={irRequest.concern_summary} onChange={(e) => setIrRequest({ ...irRequest, concern_summary: e.target.value })} /></div>
          <div><Label>Questions the staff member should answer</Label><Textarea className="min-h-28" value={irRequest.questions} onChange={(e) => setIrRequest({ ...irRequest, questions: e.target.value })} /></div>
          <div><Label>Relevant policy / process reference</Label><Input value={irRequest.policy_reference} onChange={(e) => setIrRequest({ ...irRequest, policy_reference: e.target.value })} /></div>
          <div><Label>Response due</Label><Input type="datetime-local" value={irRequest.due_at} onChange={(e) => setIrRequest({ ...irRequest, due_at: e.target.value })} /></div>
          {detail && detail.attachments.length > 0 && <div><Label>Evidence approved for staff viewing <span className="text-slate-400">(optional)</span></Label><div className="mt-2 space-y-2">{detail.attachments.map((a) => <label key={a.id} className="flex items-center gap-2 rounded border p-2 text-sm"><input type="checkbox" checked={irRequest.shared_evidence_attachment_ids.includes(a.id)} onChange={() => toggleSharedEvidence(a.id)} /> {a.filename}</label>)}</div></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setIrRequestOpen(false)}>Cancel</Button><Button onClick={requestIR} disabled={!irRequest.concern_summary.trim() || saving}>{saving ? "Requesting…" : "Issue notice & request IR"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    {staffIR && <Dialog open onOpenChange={(value) => { if (!value) setStaffIR(null); }}>
      <DialogContent className="max-h-[94dvh] max-w-4xl overflow-auto" data-testid="staff-ir-dialog">
        <DialogHeader><DialogTitle>{staffIR.notice_title} · {staffIR.case_number}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="rounded-md bg-sky-50 p-3 text-sm text-sky-900">This is the management-approved concern you are being asked to address. The complainant's identity and restricted investigation evidence are not shown here. Acknowledging receipt does not mean you agree with the concern.</div>
          <section className="rounded-lg border p-4"><h3 className="font-semibold">Concern requiring your response</h3><p className="mt-2 whitespace-pre-wrap text-sm">{staffIR.concern_summary}</p><div className="mt-3 text-xs text-slate-500">Incident: {when(staffIR.incident_at)} · Response due: {when(staffIR.due_at)}</div></section>
          {staffIR.policy_reference && <section className="rounded-lg border p-4"><h3 className="font-semibold">Relevant policy / process</h3><p className="mt-2 whitespace-pre-wrap text-sm">{staffIR.policy_reference}</p></section>}
          {staffIR.questions && <section className="rounded-lg border p-4"><h3 className="font-semibold">Questions to address</h3><p className="mt-2 whitespace-pre-wrap text-sm">{staffIR.questions}</p></section>}
          {(staffIR.shared_evidence || []).length > 0 && <section className="rounded-lg border p-4"><h3 className="mb-2 font-semibold">Evidence approved for your review</h3><div className="flex flex-wrap gap-2">{staffIR.shared_evidence!.map((a) => <Button key={a.id} variant="outline" size="sm" onClick={() => void downloadSharedEvidence(a)}><Download className="mr-1 h-4 w-4" /> {a.filename}</Button>)}</div></section>}

          {!staffIR.acknowledged_at ? <section className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4"><h3 className="font-semibold">Acknowledge receipt</h3><p className="text-xs text-slate-600">This confirms only that you received and reviewed the notice. It does not mean agreement.</p><div><Label>Type your full name</Label><Input value={ackSignature} onChange={(e) => setAckSignature(e.target.value)} /></div><Button onClick={acknowledgeIR} disabled={!ackSignature.trim()}>Acknowledge notice</Button></section> :
            <div className="text-sm font-medium text-emerald-700">Acknowledged by {staffIR.acknowledged_signature} · {when(staffIR.acknowledged_at)}</div>}

          {!staffIR.submitted_at && <section className="space-y-3 rounded-lg border p-4"><h3 className="font-semibold">Your Incident Report</h3>
            <div><Label>What happened?</Label><Textarea className="min-h-28" value={irResponse.what_happened || ""} onChange={(e) => setIrResponse({ ...irResponse, what_happened: e.target.value })} /></div>
            <div><Label>What circumstances contributed to the event?</Label><Textarea value={irResponse.contributing_factors || ""} onChange={(e) => setIrResponse({ ...irResponse, contributing_factors: e.target.value })} /></div>
            <div><Label>Is there any information management should consider?</Label><Textarea value={irResponse.information_to_consider || ""} onChange={(e) => setIrResponse({ ...irResponse, information_to_consider: e.target.value })} /></div>
            <div><Label>What corrective or preventive action do you recommend?</Label><Textarea value={irResponse.corrective_preventive_action || ""} onChange={(e) => setIrResponse({ ...irResponse, corrective_preventive_action: e.target.value })} /></div>
            <div><Label>Supporting files <span className="text-slate-400">(optional, attach before final submission)</span></Label>
              <div className="mt-2 flex flex-wrap gap-2">{(staffIR.attachments || []).map((a) => <Button key={a.id} variant="outline" size="sm" onClick={() => void downloadIRAttachment(staffIR.case_id, a)}><Download className="mr-1 h-4 w-4" /> {a.filename}</Button>)}
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-1.5 text-xs"><Paperclip className="h-4 w-4" /> Add supporting files<input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp" onChange={async (e) => { const selected = Array.from(e.target.files || []); if (!selected.length) return; await uploadIRSupportingFiles(selected); e.target.value = ""; }} /></label>
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-2"><Button variant="outline" onClick={saveIRDraft} disabled={!canRespond}>Save draft</Button><div className="flex flex-wrap items-end gap-2"><div><Label>Typed signature</Label><Input value={submitSignature} onChange={(e) => setSubmitSignature(e.target.value)} /></div><Button onClick={submitIR} disabled={!staffIR.acknowledged_at || !submitSignature.trim() || !String(irResponse.what_happened || "").trim() || saving}>{saving ? "Submitting…" : "Sign & submit IR"}</Button></div></div>
            <p className="text-xs text-amber-700">After submission, the original IR cannot be edited. Corrections or additional information are added as signed addenda.</p>
          </section>}

          {staffIR.submitted_response && <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"><h3 className="font-semibold">Submitted Incident Report</h3><div className="mt-3 space-y-2 text-sm"><p><strong>What happened:</strong> {staffIR.submitted_response.what_happened || "—"}</p><p><strong>Contributing factors:</strong> {staffIR.submitted_response.contributing_factors || "—"}</p><p><strong>Information to consider:</strong> {staffIR.submitted_response.information_to_consider || "—"}</p><p><strong>Corrective/preventive action:</strong> {staffIR.submitted_response.corrective_preventive_action || "—"}</p></div><div className="mt-3 text-xs text-slate-600">Signed by {staffIR.submitted_signature} · {when(staffIR.submitted_at)}</div></section>}
          {staffIR.submitted_at && (staffIR.attachments || []).length > 0 && <section className="rounded-lg border p-4"><h3 className="mb-2 font-semibold">Supporting files submitted with the IR</h3><div className="flex flex-wrap gap-2">{staffIR.attachments!.map((a) => <Button key={a.id} variant="outline" size="sm" onClick={() => void downloadIRAttachment(staffIR.case_id, a)}><Download className="mr-1 h-4 w-4" /> {a.filename}</Button>)}</div></section>}

          {staffIR.submitted_at && <section className="space-y-3 rounded-lg border p-4"><h3 className="font-semibold">Addendum</h3><p className="text-xs text-slate-500">Add information without changing the original submitted IR.</p>
            {(staffIR.addenda || []).map((a) => <div key={a.id} className="rounded bg-slate-50 p-3 text-sm"><p className="whitespace-pre-wrap">{a.text}</p><div className="mt-1 text-xs text-slate-500">{a.typed_signature} · {when(a.created_at)}</div></div>)}
            <Textarea value={addendum} onChange={(e) => setAddendum(e.target.value)} placeholder="Additional information" />
            <div className="flex flex-wrap items-end gap-2"><div className="flex-1"><Label>Typed signature</Label><Input value={addendumSignature} onChange={(e) => setAddendumSignature(e.target.value)} /></div><Button onClick={submitAddendum} disabled={!addendum.trim() || !addendumSignature.trim()}>Add signed addendum</Button></div>
          </section>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setStaffIR(null)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>}

    <Dialog open={closeIROpen} onOpenChange={setCloseIROpen}>
      <DialogContent data-testid="close-ir-without-response-dialog">
        <DialogHeader><DialogTitle>Close IR without staff response</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Use this only when management has documented why the IR cannot reasonably remain pending. The original request stays in the permanent case record.</p>
          <div><Label>Reason</Label><Textarea value={closeIRReason} onChange={(e) => setCloseIRReason(e.target.value)} placeholder="Example: response deadline passed and follow-up attempts were unsuccessful." /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setCloseIROpen(false)}>Cancel</Button><Button onClick={closeIRWithoutResponse} disabled={closeIRReason.trim().length < 3 || saving}>{saving ? "Closing…" : "Close IR & return to review"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={coachingOpen} onOpenChange={setCoachingOpen}>
      <DialogContent data-testid="linked-coaching-dialog">
        <DialogHeader><DialogTitle>Create linked coaching</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded bg-slate-50 p-3 text-xs text-slate-700">The coaching record receives only the sanitized Notice of Concern summary—not complainant identity, raw complaint text, or investigation evidence.</div>
          <div><Label>Title</Label><Input value={coachingForm.title} onChange={(e) => setCoachingForm({ ...coachingForm, title: e.target.value })} /></div>
          <div><Label>Management feedback</Label><Textarea value={coachingForm.feedback} onChange={(e) => setCoachingForm({ ...coachingForm, feedback: e.target.value })} /></div>
          <div><Label>Required action plan</Label><Textarea value={coachingForm.action} onChange={(e) => setCoachingForm({ ...coachingForm, action: e.target.value })} /></div>
          <div className="grid gap-3 md:grid-cols-2"><div><Label>Due date</Label><Input type="datetime-local" value={coachingForm.due_date} onChange={(e) => setCoachingForm({ ...coachingForm, due_date: e.target.value })} /></div><div><Label>Follow-up</Label><Input type="datetime-local" value={coachingForm.follow_up_at} onChange={(e) => setCoachingForm({ ...coachingForm, follow_up_at: e.target.value })} /></div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setCoachingOpen(false)}>Cancel</Button><Button onClick={createLinkedCoaching} disabled={!coachingForm.action.trim() || saving}>{saving ? "Creating…" : "Create coaching"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
